/**
 * SkeletonHeading — placeholder for heading content.
 *
 * Renders a single shimmer bar at heading height. Level controls
 * the visual size: h2 is largest, h4 is smallest.
 * Uses only CSS custom-property references.
 *
 * @see specs/integration-plan.md Section 5.1
 */
import React from "react";
import { useShimmer } from "./useShimmer";

const SKELETON_ANIMATION = "skel 1.2s ease-in-out infinite";

const SKELETON_GRADIENT =
  "linear-gradient(90deg, var(--bg2) 0%, rgba(0,0,0,0.06) 50%, var(--bg2) 100%)";

export interface SkeletonHeadingProps {
  /** Heading level controlling visual size. Default: "h3". */
  level?: "h2" | "h3" | "h4";
  /** Width of the skeleton bar. Default: "60%". */
  width?: string | number;
}

const LEVEL_HEIGHT: Record<NonNullable<SkeletonHeadingProps["level"]>, string> =
  {
    h2: "var(--fs-h2, 28px)",
    h3: "var(--fs-h3, 22px)",
    h4: "var(--fs-h4, 18px)",
  };

const shimmerBackground =
  SKELETON_GRADIENT;

export function SkeletonHeading({
  level = "h3",
  width = "60%",
}: SkeletonHeadingProps): React.ReactElement {
  const animate = useShimmer();

  return (
    <div
      data-testid="skeleton-heading"
      aria-hidden="true"
      style={{ width: typeof width === "number" ? `${width}px` : width }}
    >
      <div
        style={{
          height: LEVEL_HEIGHT[level],
          borderRadius: "var(--radius-sm, 4px)",
          background: shimmerBackground,
          backgroundSize: "200% 100%",
          animation: animate
            ? SKELETON_ANIMATION
            : "none",
        }}
      />
    </div>
  );
}
